import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import ContactUsComplaintsAndEscalations from "../../components/contact-us-complaints-and-escalations/ContactUsComplaintsAndEscalations";
import MoreFromUs from "../../components/more-from-us/more-from-us";
import Solution from "../../components/solution/Solution";
import analyticsTrigger from "../../utils/analytics/trigger/analyticsTrigger";
import { getOtherAreasContactDetails } from "../../utils/data/getContactDetails";
import getSolutionTree from "../../utils/data/getSolutionTree";
import scrollToNode from "../../utils/scrollToNode";
import { devExperiences, extractExperiences, MonetateProvider } from "../../context/MonetateContext";
import styles from "./index.module.scss";
import FAQPage from "../faq/index.page";
import contactMethods from "../../constants/contactMethods";
import InfoBanner from "@/components/info-banner/info-banner";
import FeatureFlagProvider from "../../context/FeatureFlagContext";
import { isFeatureEnabled } from "@/utils/feature-flags";

export const getServerSideProps = async ctx => {
  const experiences = extractExperiences(ctx.req, devExperiences);
  const showMaintenanceBanner = await isFeatureEnabled(ctx, "show_maintenance_banner");
  const showMIAWChat = await isFeatureEnabled(ctx, "show_miaw_chat");

  return {
    props: {
      showMaintenanceBanner,
      showMIAWChat,
      experiences,
    },
  };
};

export const metadata = {
  title: `Contact Us | Customer Services`,
  description: `Have a question? We're here to help with all your queries, from delivery and returns to orders and your online account and your My John Lewis membership.`,
};

function ContactUs({ experiences, showMaintenanceBanner, showMIAWChat }) {
  const pathname = usePathname();
  const { query } = useRouter();
  const { dt } = query;
  const [otherAreasContactDetails, setOtherAreasContactDetails] = useState(null);
  const responseIds = dt ? decodeURIComponent(dt).split("-") : [];
  const decisionTreeRootSolutionId = "contactUsWithElectricalProductSupport";
  const decisionTreeSolutions = getSolutionTree(decisionTreeRootSolutionId, responseIds);
  const decisionTreeDisableSolutions = ["productStockInformationOutStockOptions"];

  const fetchOtherAreasContactDetails = async () => {
    const otherContactDetails = await getOtherAreasContactDetails();
    if (otherContactDetails) {
      setOtherAreasContactDetails(otherContactDetails);
    }
  };

  useEffect(() => {
    analyticsTrigger(
      "dataStateChange",
      pathname,
      {
        decisionTree: {
          refinement: "",
        },
      },
      "pageload",
    );
    fetchOtherAreasContactDetails();
  }, []);
  return (
    <MonetateProvider experiences={experiences}>
      {showMaintenanceBanner && (
        <InfoBanner text="We are currently experiencing some issues with our customer service technology. Please contact us via telephone only" />
      )}
      <h1 className="page-heading" aria-label="contact us">
        Contact us
      </h1>
      <div className="util-container">
        <div className={styles["contact-us__decision-tree"]}>
          <TransitionGroup component={null}>
            {decisionTreeSolutions.map((solution, index) => (
              <CSSTransition
                key={solution.id}
                onEntered={(node, isAppearing) => {
                  if (!isAppearing) {
                    scrollToNode(node);
                  }
                }}
                addEndListener={(node, done) => {
                  node.addEventListener("transitionend", done, false);
                }}
                appear
                mountOnEnter
                unmountOnExit
              >
                <FeatureFlagProvider features={{ show_miaw_chat: showMIAWChat }}>
                  <Solution
                    index={index}
                    solution={solution}
                    className={styles["contact-us__decision-tree-solution"]}
                    contactMethods={contactMethods}
                    decisionTreeDisableSolutions={decisionTreeDisableSolutions}
                  />
                </FeatureFlagProvider>
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </div>

      <div className="util-container">
        <div className={styles.dividerContainer}>
          <div className={styles.dividerWithText}>FAQs</div>
        </div>
        <FAQPage inContactUs />
        <div className={styles.dividerContainer}>
          {otherAreasContactDetails?.complaints ? (
            <ContactUsComplaintsAndEscalations contactDetails={otherAreasContactDetails.complaints} />
          ) : null}
        </div>
        <MoreFromUs />
      </div>
    </MonetateProvider>
  );
}

ContactUs.metadata = metadata;

export default ContactUs;
